import { useEffect, useRef, useState } from 'react';
import { Pagination, Button, Tag, Modal, Form, Input, Statistic, Select, Popover } from 'antd';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import {
  confirmSalaryFileApi,
  dowloadOriginalSalaryFileApi,
  downloadSalaryExtractFileApi,
  getSalaryInfoApi,
  refreshSalaryInfoApi,
} from '@http/salary';
import { useLocation, useNavigate } from 'react-router-dom';
import SalaryInfoSearchForm from './SalaryInfoSearchForm';
import getStatus from '@utils/getStatus';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import digitNumber from '@utils/digitNumber';
import { useTranslation } from 'react-i18next';
import SalaryActionModal from './SalaryActionModal';
import {
  actionModalTypeAC,
  pickedSalaryInfoAC,
  salaryActionModalOpenAC,
  salaryInfoSearchQueryAC,
  verifyFileIdAC,
} from '@store/reducers/salary';
import { otpConfirmAC } from '@store/reducers/transactions';
import { otpLifeTimeAC } from '@store/reducers/app';
import { transactionVerifyApi, transactionVerifyResendCodeApi } from '@http/transactions';
import { getSenderAccountsApi } from '@http/accounts';
import openNotification from '@utils/openNotification';
import { formatDateTime } from '@utils/formatDate';
import { getSalaryDebitAmount, isBalanceEnough } from '@utils/salaryAmount';

const { Countdown } = Statistic;

const IN_PROGRESS_STATUSES = ['IN_PROGRESS', 'IN_VALIDATION'];

const hasActiveTransfers = (info: any) => {
  const items = info?.results || [];

  return (
    IN_PROGRESS_STATUSES.indexOf(info?.file_info?.status) !== -1 ||
    items.some((item: any) => IN_PROGRESS_STATUSES.indexOf(item?.status) !== -1)
  );
};

export const SalaryInfo = () => {
  const [drawAccountForm] = Form.useForm();
  const [confirmForm] = Form.useForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const pickedSalaryInfo = useAppSelector(state => state.salaryReducer.pickedSalaryInfo);
  const salaryInfoSearchQuery = useAppSelector(state => state.salaryReducer.salaryInfoSearchQuery);
  const verifyFileId = useAppSelector(state => state.salaryReducer.verifyFileId);
  const otpLifeTime = useAppSelector(state => state.appReducer.otpLifeTime);
  const pickedSalaryFileId = location.pathname.slice(13);
  const senderAccounts = useAppSelector(state => state.accountsReducer.senderAccounts);

  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [time, setTime] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [drawAccount, setDrawAccount] = useState<any>({});

  const pickedSalaryInfoRef = useRef(pickedSalaryInfo);
  const searchQueryRef = useRef(salaryInfoSearchQuery);
  const pageRef = useRef(1);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const changePage = (page: any) => {
    pageRef.current = page;
    dispatch(getSalaryInfoApi({ ...salaryInfoSearchQuery, file_id: pickedSalaryFileId }, page));
  };

  const reloadInfo = () => {
    dispatch(getSalaryInfoApi({ ...searchQueryRef.current, file_id: pickedSalaryFileId }, pageRef.current));
  };

  const approveFile = async () => {
    if (isSubmitting) {
      return;
    }

    if (!isBalanceEnough(drawAccount?.balance, getSalaryDebitAmount(pickedSalaryInfo?.file_info))) {
      openNotification('error', {
        code: '',
        message: 'Ошибка',
        detail: t('Insufficient funds in the account'),
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        confirmSalaryFileApi(
          {
            action: 'PROCESS',
            file_id: pickedSalaryInfo?.file_info?.file_id,
            draw_account: drawAccount.value,
          },
          {
            onOtpRequired: () => {
              setTime(false);
              setIsOtpModalOpen(true);
            },
            onDone: reloadInfo,
          }
        )
      );
    } finally {
      setIsSubmitting(false);
      dispatch(getSalaryInfoApi({ ...salaryInfoSearchQuery, file_id: pickedSalaryFileId }, 1));
    }
  };

  const verifySalaryFile = (values: any) => {
    dispatch(
      transactionVerifyApi({ code: values.code, trans_id: verifyFileId, trn_type: 'SALARY' }, () => {
        reloadInfo();
        confirmForm.resetFields();
        setIsOtpModalOpen(false);
        dispatch(otpConfirmAC(false));
        dispatch(verifyFileIdAC(''));
      })
    );
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const refreshData = () => {
    const info = pickedSalaryInfoRef.current;

    if (!info?.file_info) {
      return;
    }

    if (hasActiveTransfers(info)) {
      dispatch(refreshSalaryInfoApi({ ...searchQueryRef.current, file_id: pickedSalaryFileId }, pageRef.current));
      return;
    }

    stopPolling();
  };

  const startPolling = () => {
    if (!intervalRef.current) {
      intervalRef.current = setInterval(refreshData, 5000);
    }
  };

  useEffect(() => {
    dispatch(getSenderAccountsApi({ transaction_type: 'SALARY' }));
    if (String(pickedSalaryInfo?.file_info?.file_id) !== pickedSalaryFileId) {
      dispatch(getSalaryInfoApi({ ...salaryInfoSearchQuery, file_id: pickedSalaryFileId }, 1));
    }
  }, []);

  useEffect(() => {
    pickedSalaryInfoRef.current = pickedSalaryInfo;

    if (hasActiveTransfers(pickedSalaryInfo)) {
      startPolling();
    }
  }, [pickedSalaryInfo]);

  useEffect(() => {
    searchQueryRef.current = salaryInfoSearchQuery;
    pageRef.current = 1;
  }, [salaryInfoSearchQuery]);

  useEffect(() => {
    startPolling();

    dispatch(otpConfirmAC(false));

    return () => {
      stopPolling();
      dispatch(pickedSalaryInfoAC([]));
      dispatch(salaryInfoSearchQueryAC({}));
      dispatch(verifyFileIdAC(''));
      dispatch(otpConfirmAC(false));
    };
  }, []);

  return (
    <div>
      <div className='mb-10 flex items-center justify-between'>
        <div className='flex items-center gap-5'>
          <div
            onClick={() => navigate('/salary/list')}
            className='h-10 w-10 shrink-0 rounded-full bg-white shadow flex items-center justify-center'
          >
            <ArrowLeftOutlined style={{ fontSize: '16px' }} className='cursor-pointer' />
          </div>
          <div>
            <p className='text-base md:text-lg font-semibold'>{t('File information')}</p>
            <p className='text-sm w-[100%] text-slate-600'>{pickedSalaryInfo?.file_info?.title}</p>
          </div>
        </div>
        <div className='flex max-md:hidden'>
          {pickedSalaryInfo?.file_info?.status !== 'READY_TO_PROCEED' ? (
            <Button
              icon={<FileTextOutlined />}
              type='primary'
              className='mr-3'
              size='middle'
              onClick={() => dispatch(dowloadOriginalSalaryFileApi({ file_id: pickedSalaryFileId }))}
            >
              {t('Download file')}
            </Button>
          ) : null}
          {pickedSalaryInfo?.file_info?.status === 'SUCCESS' ? (
            <>
              <Button
                icon={<FilePdfOutlined />}
                type='primary'
                size='middle'
                onClick={() => dispatch(downloadSalaryExtractFileApi({ file_id: pickedSalaryFileId }))}
              >
                {t('Download extract')}
              </Button>
            </>
          ) : null}
          {pickedSalaryInfo?.file_info?.is_authenticator && pickedSalaryInfo?.file_info?.status === 'TO_APPROVE' ? (
            <div>
              <Button
                type='primary'
                danger
                size='middle'
                onClick={() => {
                  dispatch(salaryActionModalOpenAC(true));
                  dispatch(actionModalTypeAC('DECLINE'));
                }}
              >
                {t('Reject')}
              </Button>
              <Button
                className='ml-3'
                type='primary'
                size='middle'
                onClick={() => {
                  dispatch(salaryActionModalOpenAC(true));
                  dispatch(actionModalTypeAC('APPROVE'));
                }}
              >
                {t('Confirm')}
              </Button>
            </div>
          ) : null}
        </div>
      </div>

      <div className='md:grid grid-cols-3 gap-5 mb-7'>
        <div className='p-5 bg-[#f9f9f9] rounded-lg text-sm leading-8 text-slate-600'>
          <p>{t('Total amount')}</p>
          <p className='text-2xl font-semibold text-black'>
            {digitNumber(pickedSalaryInfo?.file_info?.total_amount)} {pickedSalaryInfo?.file_info?.trn_ccy}
          </p>
          <p className='flex justify-between'>
            <span>{t('Commission')}: </span>
            <span className='font-semibold'>
              {digitNumber(pickedSalaryInfo?.file_info?.fee_amount)} {pickedSalaryInfo?.file_info?.trn_ccy}
            </span>
          </p>
          <p className='flex justify-between'>
            <span>{t('Commission visa')}: </span>
            <span className='font-semibold'>
              {digitNumber(pickedSalaryInfo?.file_info?.visa_fee)} {pickedSalaryInfo?.file_info?.trn_ccy}
            </span>
          </p>
          <p className='flex justify-between'>
            <span>{t('Commission km')}: </span>
            <span className='font-semibold'>
              {digitNumber(pickedSalaryInfo?.file_info?.km_fee)} {pickedSalaryInfo?.file_info?.trn_ccy}
            </span>
          </p>
        </div>
        <div className='p-5 bg-[#f9f9f9] rounded-lg text-sm leading-8 text-slate-600 max-md:mt-5'>
          <p>{t('Transactions')}</p>
          <p className='text-2xl font-semibold text-black'>{pickedSalaryInfo?.file_info?.trn_count}</p>
          <div className='mt-3 grid grid-cols-2 gap-3'>
            <div className='h-18 p-2 bg-[#f0fdf4] flex items-center gap-3 rounded'>
              <CheckCircleOutlined style={{ color: 'green', fontSize: '20px' }} />
              <div className='leading-6'>
                <p className='text-sm'>{t('Successful count')}</p>
                <p className='text-xl font-semibold text-black'>{pickedSalaryInfo?.file_info?.success_count}</p>
              </div>
            </div>

            <div className='h-18 p-2 bg-[#fef2f2] flex items-center gap-3 rounded'>
              <CloseCircleOutlined className='cursor-pointer' style={{ color: 'red', fontSize: '20px' }} />
              <div className='leading-6'>
                <p className='text-sm'>{t('Not successful count')}</p>
                <p className='text-xl font-semibold text-black'>{pickedSalaryInfo?.file_info?.fail_count}</p>
              </div>
            </div>
          </div>
        </div>

        <div className='p-5 bg-[#f9f9f9] rounded text-sm leading-6 text-slate-600 max-md:mt-5'>
          <p>{t('File information')}</p>
          <div className='flex items-center gap-3'>
            <FileTextOutlined style={{ color: 'black', fontSize: '18px', fontWeight: 'bold' }} />
            <span>{getStatus(pickedSalaryInfo?.file_info?.status, 12, t)}</span>
          </div>
          <p className='mt-2'>{t('File date')}</p>
          <p className='font-semibold'>{formatDateTime(pickedSalaryInfo?.file_info?.file_date)}</p>
          <span>{t('Date of creation')}:</span>
          <p className='font-semibold'>{pickedSalaryInfo?.file_info?.created_at}</p>
          {pickedSalaryInfo?.file_info?.error_message ? (
            <div>
              <span>{t('Error')}:</span>
              <p className='font-semibold'>{pickedSalaryInfo?.file_info?.error_message}</p>
            </div>
          ) : null}
          <div className='flex md:hidden mt-5'>
            {pickedSalaryInfo?.file_info?.status !== 'READY_TO_PROCEED' ? (
              <Button
                icon={<FileTextOutlined />}
                type='primary'
                className='mr-3'
                size='small'
                onClick={() => dispatch(dowloadOriginalSalaryFileApi({ file_id: pickedSalaryFileId }))}
              >
                {t('Download file')}
              </Button>
            ) : null}
            {pickedSalaryInfo?.file_info?.status === 'SUCCESS' ? (
              <>
                <Button
                  icon={<FilePdfOutlined />}
                  type='primary'
                  size='small'
                  onClick={() => dispatch(downloadSalaryExtractFileApi({ file_id: pickedSalaryFileId }))}
                >
                  {t('Download extract')}
                </Button>
              </>
            ) : null}
            {pickedSalaryInfo?.file_info?.is_authenticator && pickedSalaryInfo?.file_info?.status === 'TO_APPROVE' ? (
              <div>
                <Button
                  type='primary'
                  danger
                  size='small'
                  onClick={() => {
                    dispatch(salaryActionModalOpenAC(true));
                    dispatch(actionModalTypeAC('DECLINE'));
                  }}
                >
                  {t('Reject')}
                </Button>
                <Button
                  className='ml-3'
                  type='primary'
                  size='small'
                  onClick={() => {
                    dispatch(salaryActionModalOpenAC(true));
                    dispatch(actionModalTypeAC('APPROVE'));
                  }}
                >
                  {t('Confirm')}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <div className='p-5 bg-[#f9f9f9] rounded-lg text-sm leading-8 text-slate-600 mb-7'>
        <p className='text-xl font-semibold text-black'>{t('Company information')}</p>
        <div className='leading-6 mt-5'>
          <p>{t('Company name')}</p>
          <p className='font-semibold'>{pickedSalaryInfo?.file_info?.customer}</p>

          <p>{t('Narrative')}</p>
          <p className='font-semibold'>{pickedSalaryInfo?.file_info?.narrative}</p>
        </div>

        {pickedSalaryInfo?.file_info?.authenticator?.length > 0 ? (
          <div className=''>
            <p className='mr-3'>{t('Authorizers')}: </p>
            {pickedSalaryInfo?.file_info?.authenticator?.map((item: any) => (
              <div className='flex items-center' key={item.authenticator_id}>
                <p className='mr-3 font-semibold'>{item.authenticator_name}</p>
                {item.authed === true ? <Tag color='success'>{t('Approved')}</Tag> : null}
                {item.authed === false ? <Tag color='error'>{t('Rejected')}</Tag> : null}
                {item.authed === null ? <Tag color='processing'>{t('Signing in')}</Tag> : null}
              </div>
            ))}
          </div>
        ) : null}

        <div>
          <Form form={drawAccountForm} name='drawAccountForm' className='mt-5' onFinish={approveFile}>
            {pickedSalaryInfo?.file_info?.status === 'READY_TO_PROCEED' ? (
              <div className='flex'>
                <Form.Item
                  name='draw_account'
                  className='sm:w-[350px] max-sm:w-[215px] mb-0'
                  rules={[{ required: true, message: `${t('Required')}` }]}
                >
                  <Select
                    allowClear
                    size='middle'
                    placeholder={`${t('Select account')}`}
                    options={senderAccounts.map(item => ({
                      label: `${item.label} - ${item.acy_avl_bal}`,
                      value: item.value,
                      balance: item.acy_avl_bal,
                    }))}
                    onChange={(value, item) => setDrawAccount(item || {})}
                  />
                </Form.Item>
                <Button
                  disabled={!pickedSalaryInfo?.file_info?.valid || isSubmitting}
                  loading={isSubmitting}
                  type='primary'
                  size='middle'
                  className='ml-3'
                  htmlType='submit'
                >
                  {t('Confirm')}
                </Button>
              </div>
            ) : null}
          </Form>
        </div>
      </div>

      <p className='my-5 text-lg font-semibold'>{t('Transactions')}</p>
      <div className='my-5 max-sm:hidden'>
        <SalaryInfoSearchForm />
      </div>

      <div className='tableHeader grid grid-cols-12 pb-5 p-5 gap-5' style={{ borderBottom: '1px solid #eee' }}>
        <p className='ml-10 col-span-2 max-sm:hidden'>{t('Date')}</p>
        <p className='col-span-2 max-sm:hidden'>{t('FIO')}</p>
        <p className='col-span-3 max-lg:col-span-3 max-sm:col-span-7'>{t('Account')}</p>
        <p className='col-span-1 max-sm:hidden'>{t('TxID')}</p>
        <p className='col-span-2 max-lg:col-span-3 text-right max-sm:col-span-5'>{t('Amount')}</p>
        <p className='col-span-2  text-right max-lg:hidden'>{t('Status')}</p>
      </div>

      {pickedSalaryInfo?.results?.length > 0 ? (
        <div>
          {pickedSalaryInfo?.results?.map((item: any) => (
            <div
              style={{ borderBottom: '1px solid #eee' }}
              className='grid grid-cols-12 items-center sm:p-5 max-sm:p-3 border-b cursor-pointer gap-5 listItem text-sm'
              key={item.id}
            >
              <div className='col-span-2 max-sm:hidden'>
                <div className='flex items-center gap-5'>
                  <div className='max-lg:hidden'>
                    {item.valid ? (
                      <CheckCircleOutlined style={{ color: 'green', fontSize: '20px' }} />
                    ) : (
                      <Popover title={item.error_message}>
                        <CloseCircleOutlined className='cursor-pointer' style={{ color: 'red', fontSize: '20px' }} />
                      </Popover>
                    )}
                  </div>
                  <span>{item.created_at?.slice(0, 10)}</span>
                </div>
              </div>
              <p className='col-span-2 max-sm:hidden'>
                {item.last_name} {item.first_name}
              </p>
              <p className='col-span-3 max-lg:col-span-3 max-sm:hidden'>
                {item.account_no} ({item.ac_ccy})
              </p>
              <div className='sm:hidden max-sm:col-span-7'>
                <p className='font-semibold'>
                  {item.last_name} {item.first_name}
                </p>
                <p className='col-span-2 max-lg:col-span-3 max-sm:col-span-7'>
                  {item.account_no} {item.ac_ccy}
                </p>
              </div>
              <p className='col-span-1 max-sm:hidden'>{item.trans_id}</p>
              <div className='col-span-2 max-lg:col-span-3 max-sm:col-span-5 text-right'>
                <p>
                  {digitNumber(item.amount)} {item.ccy}
                </p>
                <p className='mt-2 text-right lg:hidden'>{getStatus(item.status, 12, t)}</p>
              </div>
              <p className='col-span-2  text-right max-lg:hidden'>{getStatus(item.status, 12, t)}</p>
            </div>
          ))}
          <div className='mt-7 flex justify-end'>
            <Pagination
              onChange={changePage}
              current={pickedSalaryInfo.current_page}
              defaultPageSize={10}
              total={pickedSalaryInfo.total_pages * 10}
              showSizeChanger={false}
            />
          </div>
        </div>
      ) : (
        <div className='h-24 lg:h-72 flex items-center justify-center'>
          <p>{t('No Transactions')}</p>
        </div>
      )}

      <SalaryActionModal fileId={pickedSalaryFileId} />

      <Modal
        open={isOtpModalOpen}
        onOk={() => setIsOtpModalOpen(false)}
        onCancel={() => setIsOtpModalOpen(false)}
        footer={null}
      >
        <div>
          <p className='text-base'>{t('Confirm payment')}</p>
          <span className='text-xs text-slate-400'>{t('ConfirmSmsFromPhone')}</span>

          <Form form={confirmForm} name='internalOperations' onFinish={verifySalaryFile}>
            <Form.Item className='m-0' name='code' rules={[{ required: true, message: `${t('Required')}` }]}>
              <Input
                placeholder={`${t('Enter SMS code')}`}
                size='large'
                style={{ width: '100%' }}
                type='text'
                maxLength={6}
              />
            </Form.Item>
            {!time ? (
              <div className='mt-3 flex items-center text-sm text-slate-400'>
                <span className='mr-2'>{t('ResendTime')}</span>
                <Countdown
                  className='!text-xs'
                  style={{ fontSize: 12 }}
                  value={otpLifeTime}
                  onFinish={() => setTime(true)}
                  format='mm:ss'
                />
                <span className='ml-2'> {t('Second')}!</span>
              </div>
            ) : null}
            <div className='flex justify-end mt-5'>
              {time ? (
                <Button
                  className='mr-3'
                  onClick={() => {
                    dispatch(otpLifeTimeAC(Date.now() + 1000 * 30));
                    setTime(false);
                    dispatch(transactionVerifyResendCodeApi({ trans_id: verifyFileId, trn_type: 'SALARY' }));
                  }}
                >
                  {t('ResendCode')}
                </Button>
              ) : null}
              <Button type='primary' htmlType='submit'>
                {t('Confirm')}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>
    </div>
  );
};

export default SalaryInfo;
